/**
 * SpotlightTour.jsx
 * Tour guiado com spotlight sobre a interface real do app.
 * Renderizado via ReactDOM.createPortal em document.body.
 *
 * Props:
 *   tourId  {string}   — id do tour definido em data/tours.js
 *   onClose {function} — chamado ao fechar (ESC, botão fechar, conclusão)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getTourById } from '../../data/tours';
import './SpotlightTour.css';

const PAD = 12; // padding ao redor do elemento destacado (px)

function measureEl(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right };
}

/* Calcula a posição do balão baseada na posição do rect */
function calcBubbleStyle(rect, notFound) {
  if (notFound || !rect) {
    return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const BUBBLE_H = 260;
  const BUBBLE_W = 380;
  const GUTTER = 16;

  const placeAbove = rect.top > vh / 2;
  const alignRight = rect.left + rect.width / 2 > vw / 2;

  const style = { position: 'fixed' };

  if (placeAbove) {
    style.top = Math.max(GUTTER, rect.top - PAD - BUBBLE_H - 12) + 'px';
  } else {
    style.top = Math.min(rect.bottom + PAD + 12, vh - BUBBLE_H - GUTTER) + 'px';
  }

  if (alignRight) {
    style.right = Math.max(GUTTER, vw - rect.right - PAD) + 'px';
    style.left = 'auto';
  } else {
    style.left = Math.max(GUTTER, rect.left - PAD) + 'px';
    style.right = 'auto';
  }

  style.maxWidth = Math.min(BUBBLE_W, vw - GUTTER * 2) + 'px';
  return style;
}

export default function SpotlightTour({ tourId, onClose }) {
  const tour = getTourById(tourId);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect,      setRect]      = useState(null);
  const [notFound,  setNotFound]  = useState(false);
  const [closing,   setClosing]   = useState(false);
  const bubbleRef = useRef(null);
  const announcerRef = useRef(null);

  const step   = tour?.steps[stepIndex];
  const isLast = stepIndex === (tour?.steps.length ?? 1) - 1;

  /* Medir o elemento alvo a cada mudança de passo */
  useEffect(() => {
    if (!step) return;
    const measured = measureEl(step.selector);
    if (!measured) {
      setNotFound(true);
      setRect(null);
    } else {
      setNotFound(false);
      setRect(measured);
      /* Scroll suave para o elemento ficar visível */
      const el = document.querySelector(step.selector);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    /* Foca o balão para screen readers */
    const t = setTimeout(() => bubbleRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [stepIndex, step]);

  /* Re-medir ao redimensionar a janela */
  useEffect(() => {
    function onResize() {
      if (!step) return;
      const measured = measureEl(step.selector);
      if (measured) setRect(measured);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [step]);

  /* Travar scroll do body durante o tour */
  useEffect(() => {
    document.body.classList.add('spt-body-lock');
    return () => document.body.classList.remove('spt-body-lock');
  }, []);

  /* Atalhos de teclado */
  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 200);
  }, [onClose]);

  const handlePrev = useCallback(() => {
    setStepIndex(i => Math.max(0, i - 1));
  }, []);

  const handleNext = useCallback(() => {
    if (isLast) { handleClose(); return; }
    setStepIndex(i => i + 1);
  }, [isLast, handleClose]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')     { e.preventDefault(); handleClose(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); handleNext(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); handlePrev(); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose, handleNext, handlePrev]);

  /* Auto-avança quando step.autoAdvance === true e o elemento é clicado */
  useEffect(() => {
    if (!step?.autoAdvance || notFound) return;
    const el = document.querySelector(step.selector);
    if (!el) return;
    function onElClick() {
      setTimeout(() => {
        if (!isLast) setStepIndex(i => i + 1);
        else handleClose();
      }, 350);
    }
    el.addEventListener('click', onElClick, { once: true });
    return () => el.removeEventListener('click', onElClick);
  }, [stepIndex, step, notFound, isLast, handleClose]);

  if (!tour || !step) return null;

  /* ── Calcular os 4 rects do overlay ─────────────────────── */
  const topStyle    = rect ? { top: 0,                    left: 0,               width: '100vw',  height: Math.max(0, rect.top - PAD)                          } : { top: 0, left: 0, width: '100vw', height: '100vh' };
  const bottomStyle = rect ? { top: rect.bottom + PAD,    left: 0,               width: '100vw',  bottom: 0                                                     } : { display: 'none' };
  const leftStyle   = rect ? { top: rect.top - PAD,       left: 0,               width: Math.max(0, rect.left - PAD),  height: rect.height + PAD * 2           } : { display: 'none' };
  const rightStyle  = rect ? { top: rect.top - PAD,       left: rect.right + PAD, right: 0,       height: rect.height + PAD * 2                                 } : { display: 'none' };

  const ringStyle = rect ? {
    top:    rect.top    - PAD,
    left:   rect.left   - PAD,
    width:  rect.width  + PAD * 2,
    height: rect.height + PAD * 2,
  } : null;

  const bubbleStyle = calcBubbleStyle(rect, notFound);

  return ReactDOM.createPortal(
    <div className={`spt-root${closing ? ' spt-root--closing' : ''}`}>

      {/* ── 4 rects do overlay escuro ─────────────────────── */}
      <div className="spt-rect" style={topStyle}    aria-hidden="true" onClick={handleClose} />
      <div className="spt-rect" style={bottomStyle} aria-hidden="true" onClick={handleClose} />
      <div className="spt-rect" style={leftStyle}   aria-hidden="true" onClick={handleClose} />
      <div className="spt-rect" style={rightStyle}  aria-hidden="true" onClick={handleClose} />

      {/* ── Anel de destaque ──────────────────────────────── */}
      {ringStyle && (
        <div className="spt-ring" style={ringStyle} aria-hidden="true" />
      )}

      {/* ── Balão do Guto ─────────────────────────────────── */}
      <div
        className="spt-bubble-wrap"
        style={bubbleStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="spt-step-title"
        aria-describedby="spt-step-text"
        ref={bubbleRef}
        tabIndex="-1"
      >
        {/* Guto */}
        <img
          src="/mascote.png"
          alt=""
          className="spt-guto"
          aria-hidden="true"
        />

        <div className="spt-bubble">
          {/* Anunciador para screen readers */}
          <span
            ref={announcerRef}
            className="sr-only"
            aria-live="assertive"
            aria-atomic="true"
          >
            Passo {stepIndex + 1} de {tour.steps.length}: {step.titulo}
          </span>

          {/* Botão fechar */}
          <button
            className="spt-close"
            onClick={handleClose}
            aria-label="Fechar tutorial (ESC)"
          >
            ✕
          </button>

          {/* Título e texto */}
          <h2 id="spt-step-title" className="spt-bubble__title">
            {step.titulo}
          </h2>

          {/* Fallback quando elemento não encontrado */}
          {notFound && (
            <p className="spt-bubble__fallback" role="alert">
              {step.fallback ?? 'Este elemento não está visível na tela atual.'}
            </p>
          )}

          <p id="spt-step-text" className="spt-bubble__text">
            {step.texto}
          </p>

          {/* Hint de auto-avance */}
          {step.autoAdvance && !notFound && (
            <p className="spt-bubble__auto-hint" aria-live="polite">
              👆 Clique no elemento destacado para avançar
            </p>
          )}

          {/* Footer: contador + navegação */}
          <div className="spt-bubble__footer">
            <span
              className="spt-counter"
              aria-live="polite"
              aria-atomic="true"
            >
              {stepIndex + 1} / {tour.steps.length}
            </span>

            <div className="spt-nav" role="group" aria-label="Navegação do tour">
              <button
                className="spt-btn spt-btn--prev"
                onClick={handlePrev}
                disabled={stepIndex === 0}
                aria-label="Passo anterior (←)"
              >
                ← Anterior
              </button>
              {isLast ? (
                <button
                  className="spt-btn spt-btn--cta"
                  onClick={handleClose}
                >
                  Concluir ✓
                </button>
              ) : (
                <button
                  className="spt-btn spt-btn--next"
                  onClick={handleNext}
                  aria-label="Próximo passo (→)"
                >
                  Próximo →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
